import { useEffect, ChangeEvent } from 'react';
import { Stack, Box, Typography, TextField, FormControlLabel, Switch } from '@mui/material';
import { useI18nContext } from '@/i18n/i18n-react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { updateFormData, setStepErrors } from '@/store/installSlice';

export const ChurchToolsStep = () => {
    const { LL } = useI18nContext();
    const dispatch = useAppDispatch();
    const formData = useAppSelector((state) => state.install.formData);

    // Validate this step
    useEffect(() => {
        const errors: string[] = [];
        if (formData.churchToolsEnabled) {
            if (!formData.churchToolsUrl.trim()) {
                errors.push(LL.install.churchTools.errors.urlRequired());
            }
            if (!formData.churchToolsToken.trim()) {
                errors.push(LL.install.churchTools.errors.tokenRequired());
            }
        }

        dispatch(setStepErrors({ step: 4, errors }));
    }, [
        formData.churchToolsEnabled,
        formData.churchToolsUrl,
        formData.churchToolsToken,
        dispatch,
        LL,
    ]);

    const handleChange = (field: string) => (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        dispatch(updateFormData({ [field]: value }));
    };

    return (
        <Stack spacing={3}>
            <Box>
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                        mb: 2,
                    }}
                >
                    {LL.install.churchTools.description()}
                </Typography>
                <FormControlLabel
                    control={
                        <Switch
                            checked={formData.churchToolsEnabled}
                            onChange={handleChange('churchToolsEnabled')}
                        />
                    }
                    label={LL.install.churchTools.enable()}
                />
            </Box>
            {formData.churchToolsEnabled && (
                <>
                    <TextField
                        label={LL.install.churchTools.apiUrl()}
                        placeholder={LL.install.churchTools.apiUrlPlaceholder()}
                        value={formData.churchToolsUrl}
                        onChange={handleChange('churchToolsUrl')}
                        fullWidth
                    />

                    <TextField
                        label={LL.install.churchTools.apiToken()}
                        type="password"
                        value={formData.churchToolsToken}
                        onChange={handleChange('churchToolsToken')}
                        fullWidth
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.churchToolsCategoriesEnabled}
                                onChange={handleChange('churchToolsCategoriesEnabled')}
                            />
                        }
                        label={LL.install.churchTools.enableCategories()}
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.churchToolsAgendaEnabled}
                                onChange={handleChange('churchToolsAgendaEnabled')}
                            />
                        }
                        label={LL.install.churchTools.enableAgenda()}
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={formData.churchToolsFilesEnabled}
                                onChange={handleChange('churchToolsFilesEnabled')}
                            />
                        }
                        label={LL.install.churchTools.enableFiles()}
                    />
                </>
            )}
        </Stack>
    );
};
